import React, { useState, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Divider,
  Grid,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useForm, Controller } from 'react-hook-form'
import { useGetReportByIdQuery, useRegenerateReportMutation, useUpdateReportMutation } from '@/core/services/report'
import { ChipDate } from '@/components/ui/buttons/ChipButton/Chipdate'
import { Report, ReportAnswer } from '@/core/models/report.model'
import { t, Trans } from '@lingui/macro'
import { Link } from 'react-router-dom'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { useSnackbar } from 'notistack'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { getApiErrorMessage } from '@/utils/errors'

interface EditReportModalProps {
  reportId: string
}

const schema = yup.object({
  responses: yup.array(yup.object({ sectionName: yup.string().required(), response: yup.string().required(() => t`This field is required`) })).required(),
})

export function RegenerateReportButton({ report }: { report: Report }) {
  const [regenerateReport, { isLoading: isRegeneration}] = useRegenerateReportMutation()
  const authUser = useAppSelector(selectAuthUser)

  const handleRegenerateReport = async () => {
    if (!report) return

    await regenerateReport({ _id: report._id, lastUpdatedBy: authUser?._id! }).unwrap()
  }

  return (
    <Button
      variant="outlined"
      onClick={handleRegenerateReport}
      color="primary"
      disabled={isRegeneration}
      startIcon={isRegeneration ? <CircularProgress size={20} /> : undefined}
      sx={{ minWidth: 130 }}
    >
      {isRegeneration ? <Trans>Loading...</Trans> : <Trans>Regenerate</Trans>}
    </Button>
  )
}

export default function EditReportModal({ reportId }: EditReportModalProps) {
  const authUser = useAppSelector(selectAuthUser)
  const [open, setOpen] = useState(false)
  const { data: report } = useGetReportByIdQuery(reportId)
  const [updateReport, { isSuccess: isUpdated }] = useUpdateReportMutation()
  const { enqueueSnackbar } = useSnackbar()

  const { control, handleSubmit, reset } = useForm<{ responses: ReportAnswer[] }>({
    defaultValues: { responses: report?.responses },
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (isUpdated) {
      enqueueSnackbar(t`Report updated successfully`, { variant: 'success', autoHideDuration: 3000 })
    }
  }, [isUpdated])

  useEffect(() => {
    if (report) reset({ responses: report.responses })
  }, [report, reset])

  const handleSubmitReport = async (data: { responses: ReportAnswer[] }) => {
    try {
      await updateReport({
        reportId: reportId,
        responses: data.responses,
        lastUpdatedBy: authUser?._id!,
      }).unwrap()
    } catch (error :any) {
      enqueueSnackbar(getApiErrorMessage(error.message, t`An error occurred while updating the report`), { variant: 'error', autoHideDuration: 3000 })
    }
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outlined">
        <Trans>Edit</Trans>
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: '60vh' } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              <Trans>Edit the report</Trans>
            </Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {report && (
            <form onSubmit={handleSubmit(handleSubmitReport)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Link to={`/consultations/${report.consultation}`} target="_blank">
                      <Typography sx={{ textDecoration: 'underline' }} variant="h5">
                        <Tooltip title={<Trans>View consultation</Trans>} placement="top">
                          <VisibilityRoundedIcon />
                        </Tooltip>
                      </Typography>
                    </Link>
                    {report.createdAt && <ChipDate date={new Date(report.createdAt).toISOString()} />}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {report.responses?.map((section, index) => (
                    <Box key={section.sectionName} mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {section.sectionName}
                      </Typography>
                      <Controller
                        name={`responses.${index}.response`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            variant="outlined"
                            sx={{
                              mb: 2,
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Box>
                  ))}
                </Grid>
              </Grid>

              <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <RegenerateReportButton report={report} />
                <Box>
                  <Button onClick={() => setOpen(false)} color="inherit" sx={{ mr: 1 }}>
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    <Trans>Save</Trans>
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
